/**
 * User Settings Utility
 * 
 * This utility provides functions to manage user settings and authentication state.
 * Authentication data comes from sessionStorage (set during login),
 * while user preferences are stored in localStorage for persistence.
 */

import { use } from "react";

// Storage keys
const USER_PREFERENCES_KEY = 'cms_user_preferences';
const USER_SESSION_KEY = 'user';
const TOKEN_SESSION_KEY = 'token';

/**
 * Get the current user information from sessionStorage
 * @returns {Object|null} The user object or null if not found
 */
export const getUserData = () => {
  try {
    const userString = sessionStorage.getItem(USER_SESSION_KEY);
    return userString ? JSON.parse(userString) : null;
    console.error('User data not found in sessionStorage', userString);
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Get the authentication token from sessionStorage
 * @returns {string|null} The auth token or null if not found
 */
export const getAuthToken = () => {
  return sessionStorage.getItem(TOKEN_SESSION_KEY) || null;
};

/**
 * Get the current tenant ID from user data in sessionStorage
 * @returns {number|string|null} The tenant ID or null if not found
 */
export const getTenantId = () => {
  const userData = getUserData();
  // Assuming tenant_ids is an array and we want the first one
  return userData?.tenant_ids?.[0] || null;
};

/**
 * Check if the user is authenticated
 * @returns {boolean} True if the user is authenticated
 */
export const isAuthenticated = () => {
  return !!getAuthToken() && !!getUserData();
};

/**
 * Get all user preferences from localStorage
 * @returns {Object} The user preferences object (empty object if not found)
 */
export const getUserPreferences = () => {
  try {
    const preferences = localStorage.getItem(USER_PREFERENCES_KEY);
    return preferences ? JSON.parse(preferences) : {};
  } catch (error) {
    console.error('Error getting user preferences:', error);
    return {};
  }
};

/**
 * Save user preferences to localStorage
 * @param {Object} preferences - User preferences to save
 * @returns {Object} The updated preferences
 */
export const saveUserPreferences = (preferences) => {
  try {
    const currentPreferences = getUserPreferences();
    const updatedPreferences = { ...currentPreferences, ...preferences };
    localStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(updatedPreferences));
    return updatedPreferences;
  } catch (error) {
    console.error('Error saving user preferences:', error);
    return preferences;
  }
};

/**
 * Clear authentication data (logout)
 */
export const clearAuthData = () => {
  try {
    sessionStorage.removeItem(USER_SESSION_KEY);
    sessionStorage.removeItem(TOKEN_SESSION_KEY);
    // We don't clear preferences as they should persist across sessions
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }
};

/**
 * Get combined user settings (auth data + preferences)
 * This is for backward compatibility with existing code
 * @returns {Object} Combined user settings and preferences
 */
export const getUserSettings = () => {
  return {
    user: getUserData(),
    token: getAuthToken(),
    tenantId: getTenantId(),
    preferences: getUserPreferences(),
  };
};

/**
 * Update user settings - for backward compatibility
 * @param {Object} updates - Object containing fields to update
 * @returns {Object} The updated settings
 */
export const updateUserSettings = (updates) => {
  // Handle user data updates if present
  if (updates.user) {
    sessionStorage.setItem(USER_SESSION_KEY, JSON.stringify(updates.user));
  }
  
  // Handle token updates if present
  if (updates.token) {
    sessionStorage.setItem(TOKEN_SESSION_KEY, updates.token);
  }
  
  // Handle preferences updates if present
  if (updates.preferences) {
    saveUserPreferences(updates.preferences);
  }
  
  // Return updated settings
  return getUserSettings();
};

/**
 * Get the active theme preference
 * @returns {string} The theme name ('light', 'dark', or 'system')
 */
export const getThemePreference = () => {
  const preferences = getUserPreferences();
  return preferences.theme || 'system';
};

/**
 * Save the active theme preference
 * @param {string} theme - The theme name ('light', 'dark', or 'system')
 * @returns {Object} Updated preferences
 */
export const saveThemePreference = (theme) => {
  return saveUserPreferences({ theme });
};

/**
 * Get language preference
 * @returns {string} The language code (e.g., 'en-US')
 */
export const getLanguagePreference = () => {
  const preferences = getUserPreferences();
  return preferences.language || 'en-US';
};

/**
 * Save language preference
 * @param {string} language - The language code (e.g., 'en-US')
 * @returns {Object} Updated preferences
 */
export const saveLanguagePreference = (language) => {
  return saveUserPreferences({ language });
};

/**
 * Update user session activity
 * Records the last activity time to track session freshness
 */
export const updateActivityTimestamp = () => {
  saveUserPreferences({ 
    lastActivity: new Date().toISOString() 
  });
};

/**
 * Check if the session is inactive
 * @param {number} inactiveMinutes - Number of minutes to consider a session inactive
 * @returns {boolean} True if the session is inactive
 */
export const isSessionInactive = (inactiveMinutes = 30) => {
  const preferences = getUserPreferences();
  if (!preferences?.lastActivity) return true;
  
  const lastActivity = new Date(preferences.lastActivity).getTime();
  const currentTime = new Date().getTime();
  const inactiveTime = inactiveMinutes * 60 * 1000;
  
  return (currentTime - lastActivity) > inactiveTime;
};

// Default export with all functions
export default {
  getUserData,
  getAuthToken,
  getTenantId,
  isAuthenticated,
  getUserPreferences,
  saveUserPreferences,
  clearAuthData,
  getUserSettings,
  updateUserSettings,
  getThemePreference,
  saveThemePreference,
  getLanguagePreference,
  saveLanguagePreference,
  updateActivityTimestamp,
  isSessionInactive
};